<template>
  <div class="benchmark">
    <ul class="navigation" v-if="mainTechnologies && mainTechnologies.length > 0">
      <li
        :class="`navigation-item ${-1 === categoryActive ? 'active' : ''}`"
        @click="selectTechnology(null, null)"
      >
        See All
      </li>
      <li
        v-for="(item, i) in mainTechnologies"
        :key="`${i}`"
        :class="`navigation-item ${i === categoryActive ? 'active' : ''}`"
        @click="selectTechnology(item, i)"
      >
        {{ item.displayName }}
      </li>
    </ul>

    <!-- Mobile -->
    <div
      class="navigation-mobile main"
      @blur="categoryOpen = false"
      v-if="mainTechnologies && mainTechnologies.length > 0"
    >
      <div class="selected" :class="{ open: categoryOpen }" @click="categoryOpen = !categoryOpen">
        <span v-if="showCategoryIntialText">Select Technology</span>
        <span v-else>{{ categorySelected }}</span>
      </div>
      <div v-if="mainTechnologies" class="items" :class="{ selectHide: !categoryOpen }">
        <div @click="selectTechnology(null, null)">See All</div>
        <div v-for="(item, i) in mainTechnologies" :key="`${i}`" @click="selectTechnology(item, i)">
          {{ item.displayName }}
        </div>
      </div>
    </div>

    <!-- Mobile Sub Category -->
    <div v-if="hasSubTechnologies" class="navigation-mobile" @blur="subCategoryOpen = false">
      <div
        class="selected italics"
        :class="{ open: subCategoryOpen }"
        @click="subCategoryOpen = !subCategoryOpen"
      >
        <span v-if="showSubCategoryIntialText">Select a Subcategory</span>
        <span v-else>{{ subcategorySelected }}</span>
      </div>

      <div class="items" :class="{ selectHide: !subCategoryOpen }">
        <div
          v-for="(option, i) of subCategoryNames"
          :key="i"
          @click="selectSubTechnology(option, i)"
        >
          {{ option }}
        </div>
      </div>
    </div>

    <div class="results">
      <div class="filter">
        <div class="filter-container">
          <div class="box" v-if="!loading && hasSubTechnologies">
            <p class="filter__title">
              Subcategories for <span>{{ categorySelected }}</span>
            </p>
            <div class="filter__options">
              <label
                class="c-radio"
                v-for="(option, i) of subCategoryNames"
                :key="`subtechnologies-${i}`"
                :for="`subtechnologies-${i}`"
                @click="selectSubTechnology(option, i)"
              >
                <input
                  type="radio"
                  name="subtechnologies"
                  :id="`subtechnologies-${i}`"
                  :value="`subtechnologies-${i}`"
                  :checked="subCategoryIsChecked(i)"
                />
                <span>{{ option }}</span>
              </label>
            </div>
          </div>

          <div class="box filter-by" v-if="!loading">
            <p class="filter__title"><span>Filter by</span></p>
            <div class="filter__options checkbox">
              <label class="c-checkbox" for="images" @click="selectImages($event)">
                <input type="checkbox" id="images" :checked="imagesIsChecked()" />
                <span>CIS Hardened Images</span>
              </label>
              <label class="c-checkbox" for="prov4" @click="selectCisCatPro($event)">
                <input type="checkbox" id="prov4" :checked="proV4IsChecked()" />
                <span>CIS-CAT Pro</span>
              </label>
            </div>
          </div>

          <div class="left-callout">
            <sc-text tag="h3" v-if="fields && fields.calloutTitle" :field="fields.calloutTitle" />
            <sc-text tag="p" v-if="fields && fields.calloutDesc" :field="fields.calloutDesc" />
            <sc-link
              v-if="
                fields &&
                fields.calloutCta &&
                fields.calloutCta.value &&
                fields.calloutCta.value.text
              "
              :field="fields.calloutCta"
              class="c-cta--secondary"
            />
          </div>
        </div>
      </div>

      <div class="list">
        <div v-if="loading">
          <p>Loading benchmarks...</p>
        </div>
        <div v-else>
          <div class="list__label" v-if="hasSubTechnologies">
            Showing latest CIS Benchmarks for "<span>{{ subcategorySelected }}</span
            >" in “<span>{{ categorySelected }}</span
            >”
          </div>
          <div class="list__label" v-else>
            Showing latest CIS Benchmarks for "<span>{{ categorySelected }}</span
            >"
          </div>
          <div class="noBenchmarks" v-if="list && list.length === 0">
            <sc-rich-text
              class="noBenchmarks__content"
              v-if="fields && fields.noBenchmarks"
              :field="fields.noBenchmarks"
            />
          </div>
          <div class="accordion" v-if="list">
            <div
              class="accordion__item"
              v-for="(item, index) in list"
              :key="`accordion${index}`"
              :class="{ open: isOpen.includes(index) }"
            >
              <div class="accordion__item--title" @click="toggle(index)">
                <h4>{{ item.displayName }}</h4>
              </div>
              <div class="accordion__item--content">
                <a
                  class="cta"
                  href="https://learn.cisecurity.org/benchmarks"
                  :tech_key="`${item.technologyKey}`"
                  :tech_version="`${item.latestTechnologyVersion}`"
                  :version="`${item.version[0]?.benchmarkVersion}`"
                  @click="setRecentlyViewedBenchmarksCookie"
                >
                  Download The Benchmark
                </a>
                <div class="versions">
                  Available versions include:<br />
                  <ul v-if="item && item.benchmarkVersions">
                    <li v-for="benchmark in item.benchmarkVersions" :key="benchmark.id">
                      {{ benchmark.benchmarkTitle }} ({{ benchmark.benchmarkVersion }})
                    </li>
                  </ul>
                </div>
                <p>
                  To further explore this Benchmark, <a :href="`${item.url}`">click here</a>. If you
                  want to tailor the security recommendations of this Benchmark, you can do so using
                  a <a href="/cis-securesuite">CIS SecureSuite Membership</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <sc-placeholder name="jss-benchmark-list" :rendering="rendering" />
      </div>
    </div>
  </div>
</template>

<script>
import { Text, RichText, Link, Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import benchmarkData from './CIS-Benchmarks-List.graphql';
import { useQuery } from '@vue/apollo-composable';
import { useCookies } from 'vue3-cookies';

export default {
  name: 'CIS-Resource-Benchmark-List',
  components: {
    ScText: Text,
    ScRichText: RichText,
    ScLink: Link,
    ScPlaceholder: Placeholder
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },
  data: function () {
    return {
      loadingKey: 0,
      isOpen: [0],
      categoryOpen: false,
      categorySelected: 'See All',
      categoryActive: -1,
      subcategorySelected: 'See All',
      subcategoryActive: 0,
      subCategoryOpen: false,
      prov4Selected: false,
      imagesSelected: false,
      showCategoryIntialText: true,
      showSubCategoryIntialText: true,
      subTechnologies: null,
      selectedTechnology: null,
      selectedSubTechnology: null
    };
  },
  setup(props) {
    const { result, loading, error } = useQuery(benchmarkData);
    const { cookies } = useCookies();

    return {
      result,
      loading,
      error,
      cookies
    };
  },
  updated() {
    this.updateNavigationPosition();
  },
  computed: {
    mainTechnologies() {
      if (this.loading) return undefined;
      return this.result?.technologies?.children;
    },
    list() {
      if (this.loading) return undefined;

      // Utility functions
      const cleanName = (name) => {
        return name.replace('CIS', '').replace('Benchmark', '');
      };

      const getBenchmarkVersion = (version) => {
        const versions = version
          .filter((item, index, array) => {
            const topItem = array
              .filter(
                (a) =>
                  cleanName(a?.benchmarkTitle?.value) === cleanName(item?.benchmarkTitle?.value)
              )
              .sort((a, b) => {
                const versionA = a?.benchmarkVersion?.value;
                const versionB = b?.benchmarkVerison?.value;
                if (versionA < versionB) return -1;
                if (versionA > versionB) return 1;
                return 0;
              })[0];
            return (
              topItem?.benchmarkVersion?.value === item?.benchmarkVersion?.value &&
              topItem?.benchmarkTitle?.value === item?.benchmarkTitle?.value
            );
          })
          .sort((a, b) => {
            const versionA = a?.published?.value;
            const versionB = b?.published?.value;
            if (versionA < versionB) return 1;
            if (versionA > versionB) return -1;
            return 0;
          })
          .map((s) => ({
            benchmarkTitle: cleanName(s?.benchmarkTitle?.value),
            benchmarkVersion: s?.benchmarkVersion?.value,
            published: s?.published?.value,
            proV4: s?.proV4?.value,
            images: s?.images?.targetItems
          }));

        return versions;
      };

      const sortByDisplayName = (a, b) => {
        if (a?.displayName?.toLowerCase() > b?.displayName?.toLowerCase()) return 1;
        if (a?.displayName?.toLowerCase() < b?.displayName?.toLowerCase()) return -1;
        return 0;
      };

      // Create array of technologies for filtering the list of benchmarks
      let technologyKeys = [];
      if (this.categoryActive != -1) {
        const category = this.result?.technologies?.children?.filter(
          (c) => c.displayName === this.categorySelected
        )[0];

        if (this.subcategoryActive != 0) {
          const subcategory = category?.children?.filter(
            (c) => c.displayName === this.subcategorySelected
          )[0];
          technologyKeys = subcategory?.benchmarks?.targetItems?.map((c) => c.technologyKey.value);
        } else {
          technologyKeys = category?.benchmarks?.targetItems?.map((c) => c.technologyKey.value);
        }
      }

      const prov4 = (item) => {
        if (this.prov4Selected) {
          return item.benchmarkVersions.targetItems.some((v) => v.proV4.value === '1');
        }
        return item;
      };

      const images = (item) => {
        if (this.imagesSelected) {
          return item.benchmarkVersions.targetItems.some((v) => v.images.targetItems.length != 0);
        }
        return item;
      };

      // List of benchmarks filtered by the selected technology category/subcategory
      const benchmarks = this.result?.benchmarks?.children
        .filter(prov4)
        .filter(images)
        .map((l) => ({
          displayName: l?.resourceTitle?.value,
          latestTechnologyVersion: l?.latestTechnologyVersion?.value,
          url: l?.url,
          technologyKey: l?.technologyKey?.value,
          version: getBenchmarkVersion(l?.benchmarkVersions?.targetItems),
          benchmarkVersions: getBenchmarkVersion(l?.benchmarkVersions?.targetItems)
        }))
        .filter((c) => technologyKeys?.length === 0 || technologyKeys?.includes(c?.technologyKey))
        .sort(sortByDisplayName);

      return benchmarks;
    },
    hasSubTechnologies() {
      return (
        this.subTechnologies &&
        this.subTechnologies[0] &&
        this.subTechnologies[0].children &&
        this.subTechnologies[0].children.length > 0
      );
    },
    subCategoryNames() {
      let subcategory = [];

      if (this.hasSubTechnologies) {
        subcategory = {
          ...this.subTechnologies.map((item) => item.children.map((x) => x.displayName))
        }[0];

        subcategory.unshift('See All');
      }

      return subcategory;
    }
  },
  methods: {
    toggle(index) {
      if (this.isOpen.includes(index)) {
        this.isOpen = this.isOpen.filter((i) => i !== index);
        return;
      }

      this.isOpen.push(index);
    },
    selectTechnology(item, index) {
      // Reset values
      this.categoryActive = index;
      this.categorySelected = item?.displayName;
      this.categoryOpen = false;
      this.subcategorySelected = 'See All';
      this.subcategoryActive = 0;
      this.subCategoryOpen = false;
      this.selectedSubTechnology = null;
      this.isOpen = [0];
      this.showCategoryIntialText = false;
      this.showSubCategoryIntialText = true;

      // If null select all technologies (See All)
      if (item === null && index === null) {
        this.categoryActive = -1;
        this.categorySelected = 'See All';
      }

      this.subTechnologies = this.result?.technologies?.children
        .filter((c) => c?.displayName === item?.displayName || '')
        .map((l) => ({ children: l?.children }));

      this.$nextTick(() => {
        this.updateSubNavigationPosition();
      });
    },
    selectSubTechnology(option, index) {
      // Reset values
      this.subCategoryOpen = false;
      this.isOpen = [0];
      this.showSubCategoryIntialText = false;
      this.subcategorySelected = option;
      this.subcategoryActive = index;
    },
    selectCisCatPro(event) {
      event.preventDefault();
      this.prov4Selected = !this.prov4Selected;
    },
    selectImages(event) {
      event.preventDefault();
      this.imagesSelected = !this.imagesSelected;
    },
    setRecentlyViewedBenchmarksCookie(data) {
      var technologyKey = data?.target?.getAttribute('tech_key');
      var technologyVersion = data?.target?.getAttribute('tech_version');
      var version = data?.target?.getAttribute('version');
      var href = data?.target?.getAttribute('href');

      var recentlyViewedBenchmarks = Cookies.getJSON('recentlyViewedBenchmarks') || [];

      var exists = false;

      for (var z = 0; z < recentlyViewedBenchmarks.length; z++) {
        var benchmark = recentlyViewedBenchmarks[z];

        if (
          benchmark.technologyKey === technologyKey &&
          benchmark.technologyVersion === technologyVersion &&
          benchmark.version === version
        ) {
          exists = true;
        }
      }

      if (!exists) {
        if (recentlyViewedBenchmarks.length >= 5) {
          recentlyViewedBenchmarks.splice(-1, 1);
        }
        recentlyViewedBenchmarks.unshift({
          technologyKey: technologyKey,
          technologyVersion: technologyVersion,
          version: version
        });

        if (Cookiebot?.consent?.preferences)
          Cookies.set('recentlyViewedBenchmarks', recentlyViewedBenchmarks, {
            domain: '.cisecurity.org'
          });

        window.location = href;
      }
    },
    updateNavigationPosition() {
      var banner = this.cookies?.get('banner');
      var navigation = document.querySelector('.navigation');
      var navigationMobile = document.querySelector('.main');
      var bannerItem = document.querySelector('.c-banner');

      if (banner === null || banner != 'closed') {
        if (navigation && bannerItem && navigationMobile) {
          var top = bannerItem.offsetHeight;
          navigation.style.top = top + 'px';
          navigationMobile.style.top = top + 'px';
        }
      }
    },
    updateSubNavigationPosition() {
      var banner = this.cookies?.get('banner');
      var navigationSubMobile = document.querySelector('.filter');
      var bannerItem = document.querySelector('.c-banner');

      if (banner === null || banner != 'closed') {
        if (navigationSubMobile && bannerItem) {
          var top = bannerItem.offsetHeight;
          if (document.body.clientWidth <= 767) {
            navigationSubMobile.style.top = top + 88 + 'px';
          }
        }
      }
    },
    subCategoryIsChecked(i) {
      return i === this.subcategoryActive;
    },
    imagesIsChecked() {
      return !!this.imagesSelected;
    },
    proV4IsChecked() {
      return !!this.prov4Selected;
    }
  }
};
</script>
<style lang="scss" scoped>
.benchmark {
  @include padding-x-thin;
  .navigation {
    display: none;
    position: sticky;
    top: -0.1rem;
    z-index: 2;
    background: $white;

    @include tablet-up {
      border-top: 0.2rem solid $box-gray;
      list-style: none;
      display: flex;
      justify-content: space-between;
      margin: 0;
    }

    &-item {
      position: relative;
      display: inline-block;
      @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);
      text-transform: uppercase;
      padding: 1.4rem 0;

      @include tablet-up {
        margin-right: 1.5rem;
        font-size: 1.4rem;
        line-height: 1.8rem;
        text-align: center;
      }

      @include desktop-up {
        font-size: 1.8rem;
        line-height: 2.2rem;
      }

      @include large-desktop-up {
        text-align: left;
      }

      &:hover {
        cursor: pointer;
        top: -0.3rem;
      }

      &.active {
        &:before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 0.3rem;
          background: $orange;
        }

        &:hover {
          top: 0;
        }
      }

      &:last-child {
        @include tablet-up {
          margin-right: 0;
        }
      }
    }
  }
  .results {
    @include tablet-up {
      display: flex;
      margin-top: 3rem;
    }
    .filter {
      background: $white;

      @include tablet-up {
        width: 25%;
        margin-right: 4.5rem;
      }

      &-container {
        @include tablet-up {
          position: sticky;
          top: 8.8rem;
          z-index: 1;
        }
      }

      .box {
        display: none;

        @include tablet-up {
          display: block;
          background-color: $white;
          padding: 2rem;
          margin-bottom: 2rem;
          border: 0.5px solid #f3f4f4;
          box-shadow: 0 16px 16px 0 rgba(0, 0, 0, 0.04), 0 4px 8px 0 rgba(0, 0, 0, 0.04);
        }

        &.filter-by {
          display: block;
          padding: 2rem 1.6rem;
          margin-bottom: 4rem;
          background-color: $white;
          border: 0.1rem solid $box-gray;
          box-shadow: 0 16px 16px 0 rgba(0, 0, 0, 0.04), 0 4px 8px 0 rgba(0, 0, 0, 0.04);
        }
      }

      &__title {
        display: block;
        margin-bottom: 2rem;
        @include font-styles('Arial', 2rem, 200, normal, 2.8rem, 0, $blue);

        span {
          display: block;
          font-weight: 500;
        }
      }

      &__options {
        margin: 0;

        input {
          display: none;
        }

        label {
          display: block;
          position: relative;
          padding-left: 4rem;
          margin-bottom: 2rem;
          cursor: pointer;

          &:last-child {
            margin-bottom: 0;
          }

          span {
            display: inline-block;
            @include font-styles('Arial', 1.8rem, 200, normal, 2.2rem, 0, $gray);

            @include tablet-up {
              font-size: 1.6rem;
              line-height: 2rem;
            }

            @include desktop-up {
              font-size: 1.8rem;
              line-height: 2.2rem;
            }

            &:before {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              -webkit-transform: translatey(-50%);
              transform: translatey(-50%);
              width: 2.5rem;
              height: 2.5rem;
              border-radius: 50%;
              background-color: $white;
              transition: all 0.25s linear;
              border: 0.15rem solid $blue;
            }
          }

          &.c-radio {
            input:checked + span:after {
              content: '';
              position: absolute;
              left: 0.5rem;
              top: 50%;
              -webkit-transform: translatey(-50%);
              transform: translatey(-50%);
              width: 1.5rem;
              height: 1.5rem;
              border-radius: 50%;
              background-color: $blue;
              transition: all 0.25s linear;
            }
          }

          &.c-checkbox {
            span {
              &::before {
                top: 1.25rem;

                @include tablet-up {
                  top: 1.6rem;
                }
              }
            }

            input:checked + span:after {
              content: '';
              position: absolute;
              left: 0.3rem;
              top: 0.2rem;
              color: $white;
              transition: all 0.25s linear;
              background-image: url(~@/assets/images/icon-checkbox-checkmark.svg);
              background-size: contain;
              width: 2rem;
              height: 2rem;

              @include tablet-up {
                top: 0.6rem;
              }
            }
          }
        }

        &.checkbox {
          label {
            span {
              &:before {
                border-radius: 0;
              }
            }

            input:checked + span:after {
              border-radius: 0;
            }
          }
        }
      }

      &__button {
        display: block;
        width: 100%;
        border: none;
        background: $white;
        padding: 2rem;
        margin-top: 1.5rem;
        @include font-styles('Arial', 2rem, 500, normal, 2.6rem, 0, $blue);

        &.active {
          background: $blue;
          color: $white;
        }
      }
    }

    .left-callout {
      background: $white;

      h3 {
        color: $dark-blue;
      }

      p {
        line-height: 2.6rem;
        margin-bottom: 2.4rem;
      }
    }

    .list {
      width: 100%;

      &__label {
        @include font-styles('Arial', 1.4rem, 200, normal, 2rem, 0, $gray);
        padding: 4rem 1rem 1.8rem 0;

        @include tablet-up {
          @include font-styles('Arial', 1.6rem, 200, normal, 2.4rem, 0, $gray);
          padding: 0 0 3rem 0;
        }
      }

      span {
        font-weight: 500;
      }

      .noBenchmarks {
        display: flex;
        text-align: center;
        align-items: center;
        justify-content: center;
        text-align: center;
        margin: 12rem 0;

        &__content {
          width: 90%;

          @include tablet-up {
            width: 85%;
          }

          @include desktop-up {
            width: 52%;
          }

          &:deep() {
            h3 {
              color: $dark-blue;
            }

            p {
              color: $gray;
            }
          }
        }
      }
    }
  }

  .accordion {
    margin-bottom: 5rem;

    &__item {
      border-bottom: 0.2rem solid $divider;

      &.open {
        background: $box-gray;

        div {
          &:first-child {
            &:before {
              transform: translate(3.8rem, -0.7rem) rotate(135deg);
            }
          }

          display: block;
        }
      }

      &--title {
        width: 100%;
        position: relative;
        padding: 2rem 4rem 2.4rem 1rem;
        cursor: pointer;

        @include tablet-up {
          padding: 2rem 4rem 2.4rem 2rem;
        }

        &:hover {
          background: $box-gray;
        }

        &:before {
          font-weight: 700;
          font-size: 2rem;
          content: '∟';
          transform: translate(3.5rem, -1.67rem) rotate(-45deg);
          color: $blue;
          position: absolute;
          top: 50%;
          right: 5rem;
          pointer-events: none;
        }
      }

      &--content {
        padding: 0 1rem 1rem 1rem;
        display: none;

        @include tablet-up {
          padding: 0 4rem 2.2rem 4rem;
        }

        .cta {
          display: inline-flex;
          padding: 0.5rem 1.1rem 0.5rem 1.1rem;
          text-transform: uppercase;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 2.5rem;
          @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);
          background: transparent;
          border: none;
          border-left: solid 0.4rem $blue;

          &:after {
            content: '';
            position: relative;
            background-image: url(~@/assets/images/icon-arrow-right-blue-sm.svg);
            background-repeat: no-repeat;
            background-size: contain;
            margin-left: 1rem;
            width: 1.9rem;
            min-width: 1.9rem;
            height: 1.5rem;
            min-height: 1.5rem;
            transition: 0.25s;
            left: 0;
          }

          &:hover,
          &:focus,
          &:active {
            color: $blue;
            text-decoration: none;
          }

          &:hover {
            &:after {
              left: 1rem;
            }
          }
        }

        .versions {
          @include font-styles('Arial', 1.4rem, 500, normal, 2rem, 0, $gray);
          margin-bottom: 2rem;

          p {
            @include font-styles('Arial', 1.4rem, 500, normal, 2rem, 0, $gray);
            margin-bottom: 0;
          }

          ul {
            display: inline-block;
            margin: 0;
            line-height: 1.8rem;

            li {
              @include font-styles('Arial', 1.4rem, 500, normal, 2.2rem, 0, $gray);
              margin: 0 0.7rem 0 0;
              padding: 0 0.5rem 0 0;
              display: inline;
              position: relative;
              list-style: none;
              border-right: solid 0.1rem $gray;

              &:before {
                display: none;
              }

              &:last-child {
                padding-left: 0;
                border-right: none;
              }
            }
          }
        }
      }

      &.scEnabledChrome {
        .accordion__item--content {
          display: block;
        }
      }
    }

    h4 {
      @include font-styles('Arial', 1.8rem, 300, normal, 2.4rem, 0, $blue);
      margin: 0;

      @include tablet-up {
        @include font-styles('Arial', 2.8rem, 300, normal, 3.5rem, 0, $blue);
      }

      &::before {
        display: none;
      }
    }

    h5 {
      font-size: 2rem;
      font-weight: 500;
      padding: 0;
      margin: 0 0 1.5rem 0;
    }

    ul,
    &__unordered-list {
      @include font-styles('Arial', 2rem, 200, normal, 3rem, 0, $gray);
      margin: 0 0 1.5rem 2rem;
      padding: 0;
      list-style: none;

      li {
        margin: 0 0 0 0.5rem;
        position: relative;

        &:before {
          content: '';
          transform: translateY(-50%);
          display: inline-block;
          width: 0.8rem;
          height: 0.8rem;
          border-radius: 4rem;
          background-color: $orange;
          margin-left: -2.5rem;
          margin-right: 2.5rem - 0.8rem;
        }
      }
    }

    &.scEnabledChrome {
      > div {
        padding-bottom: 3rem;
      }
    }
  }
}

.navigation-mobile {
  position: relative;
  width: 100%;
  text-align: left;
  outline: none;
  margin-bottom: 0.8rem;

  @include tablet-up {
    display: none;
  }

  &.main {
    position: sticky;
    top: -0.1rem;
    z-index: 2;
    background: $white;
  }

  &.submain {
    position: sticky;
    top: 8.8rem;
    z-index: 1;
  }

  .selected {
    background-color: $white;
    padding: 2.8rem 1.6rem;
    cursor: pointer;
    user-select: none;
    border-bottom: solid 0.2rem $orange;
    box-shadow: 0 -0.2rem 0.7rem 0 rgba(0, 0, 0, 0.07), 0 1.8rem 1.7rem 0 rgba(0, 0, 0, 0.08),
      0 0.2rem 0.4rem 0 rgba(0, 0, 0, 0.08);
    @include font-styles('Arial', 1.8rem, 500, normal, 2.4rem, 0, $blue);

    &.italics {
      font-style: italic;
    }

    &:after {
      font-weight: 700;
      font-size: 2rem;
      content: '∟';
      transform: translate(3.5rem, -1.67rem) rotate(-45deg);
      color: $blue;
      position: absolute;
      top: 50%;
      right: 5rem;
      pointer-events: none;
    }

    &.open {
      &:after {
        transform: translate(3.8rem, -0.7rem) rotate(135deg);
      }
    }
  }

  .items {
    overflow: hidden;
    position: absolute;
    background-color: $white;
    left: 0;
    right: 0;
    z-index: 1;
    margin-top: -2rem;
    box-shadow: 0 -2px 7px -10px rgba(0, 0, 0, 0.07), 0 18px 17px 0 rgba(0, 0, 0, 0.08),
      0 2px 4px 0 rgba(0, 0, 0, 0.08);
    border-bottom: solid 0.2rem $orange;

    div {
      @include font-styles('Arial', 1.6rem, 200, normal, 2rem, 0, $gray);
      padding: 1.6rem 2rem;
      cursor: pointer;
      user-select: none;

      &:hover {
        color: $gray;
        font-weight: 500;
        background-color: $box-gray;
      }
    }
  }
}

.selectHide {
  display: none;
}
</style>
